// Localização: frontend/src/components/Financeiro/ModalMesclarContas.jsx
// Modal para mesclar (agrupar) múltiplas transações

import React, { useState, useEffect } from 'react';
import Modal from '../UI/Modal';
import PrimaryButton from '../Common/PrimaryButton';
import Alert from '../UI/Alert';
import { Merge, X } from 'lucide-react';
import moment from 'moment';

/**
 * Modal para mesclar transações
 * @param {boolean} isOpen - Se o modal está aberto
 * @param {Function} onClose - Função para fechar o modal
 * @param {Array} transacoesSelecionadas - Array de transações selecionadas
 * @param {Function} onMesclar - Callback quando a mesclagem é confirmada
 */
const ModalMesclarContas = ({ 
    isOpen, 
    onClose, 
    transacoesSelecionadas = [],
    onMesclar 
}) => {
    const [nome, setNome] = useState('');
    const [descricao, setDescricao] = useState('');
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Calcular valores iniciais quando transações mudam
    useEffect(() => {
        if (transacoesSelecionadas.length > 0) {
            // Encontrar data mínima e máxima
            const datas = transacoesSelecionadas
                .map(t => t.data_transacao)
                .filter(d => d)
                .sort();
            
            if (datas.length > 0) {
                setDataInicio(moment(datas[0]).format('YYYY-MM-DD'));
                setDataFim(moment(datas[datas.length - 1]).format('YYYY-MM-DD'));
            }

            // Sugerir nome baseado na data
            if (!nome && datas.length > 0) {
                const primeiraData = moment(datas[0]);
                setNome(`Agrupamento ${primeiraData.format('MM/YYYY')}`);
            }
        }
    }, [transacoesSelecionadas]);

    // Calcular valor total
    const valorTotal = transacoesSelecionadas.reduce((sum, t) => sum + (parseFloat(t.valor) || 0), 0);

    // Formatar valor
    const formatarValor = (valor) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor);
    };

    // Validar e submeter
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validações
        if (!nome.trim()) {
            setError('Nome é obrigatório');
            return;
        }

        if (transacoesSelecionadas.length < 2) {
            setError('Selecione pelo menos 2 transações para mesclar');
            return;
        }

        if (!dataInicio || !dataFim) {
            setError('Data início e data fim são obrigatórias');
            return;
        }

        if (new Date(dataFim) < new Date(dataInicio)) {
            setError('Data fim deve ser maior ou igual à data início');
            return;
        }

        setLoading(true);
        try {
            await onMesclar({
                nome: nome.trim(),
                descricao: descricao.trim() || null,
                transacao_ids: transacoesSelecionadas.map(t => t.id),
                data_inicio: dataInicio,
                data_fim: dataFim
            });
            
            // Limpar formulário
            setNome('');
            setDescricao('');
            setDataInicio('');
            setDataFim('');
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Erro ao mesclar transações');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Mesclar Transações"
            size="xl"
        >
            <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                    <Alert type="error" message={error} />
                )}

                {/* Resumo das transações selecionadas */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-700">
                            Transações Selecionadas
                        </h3>
                        <span className="text-xs text-gray-500">
                            {transacoesSelecionadas.length} transação(ões)
                        </span>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                        {transacoesSelecionadas.map((transacao) => (
                            <div 
                                key={transacao.id}
                                className="flex items-center justify-between text-sm bg-white p-2 rounded border border-gray-200"
                            >
                                <div className="flex-1">
                                    <span className="font-medium text-gray-900">
                                        {transacao.descricao}
                                    </span>
                                    <div className="text-xs text-gray-500 mt-1">
                                        {moment(transacao.data_transacao).format('DD/MM/YYYY')} • {transacao.categoria?.nome}
                                    </div>
                                </div>
                                <span className={`font-semibold ml-4 ${
                                    transacao.tipo === 'Receita' ? 'text-green-600' : 'text-red-600'
                                }`}>
                                    {transacao.tipo === 'Receita' ? '+' : '-'} {formatarValor(transacao.valor)}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-700">
                                Valor Total:
                            </span>
                            <span className="text-lg font-bold text-emerald-600">
                                {formatarValor(valorTotal)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Campos do formulário */}
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nome do Agrupamento <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={nome}
                            onChange={(e) => setNome(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            placeholder="Ex: Agrupamento Janeiro 2025"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Descrição (opcional)
                        </label>
                        <textarea
                            value={descricao}
                            onChange={(e) => setDescricao(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            placeholder="Descrição do agrupamento..."
                            rows={3}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Data Início <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                value={dataInicio}
                                onChange={(e) => setDataInicio(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Data Fim <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                value={dataFim}
                                onChange={(e) => setDataFim(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                required
                            />
                        </div>
                    </div>
                </div>

                {/* Botões */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50"
                    >
                        Cancelar
                    </button>
                    <PrimaryButton
                        type="submit"
                        disabled={loading || transacoesSelecionadas.length < 2}
                        className="flex items-center gap-2"
                    >
                        <Merge className="h-4 w-4" />
                        {loading ? 'Mesclando...' : 'Mesclar Transações'}
                    </PrimaryButton>
                </div>
            </form>
        </Modal>
    );
};

export default ModalMesclarContas;

